// Daily goals card (calories, macros, taler, calendar sync, sleep defaults) — extracted from pages/Settings.jsx.
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Target, Save, Coins, Moon } from 'lucide-react';
import { CalendarSubscriptionCard } from '../CalendarSubscriptionCard';

export function GoalsCard({ t, language, profileForm, setProfileForm, handleSave, loading, optimalCal }) {
    return (
        <>
                {/* Goals Card */}
                <Card>
                    <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                            <Target className="w-5 h-5" />
                            {t('daily_goals')}
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="calorie_goal">{t('calories_kcal')}</Label>
                                <Input
                                    id="calorie_goal"
                                    type="number"
                                    value={profileForm.calorie_goal}
                                    onChange={(e) => setProfileForm({ ...profileForm, calorie_goal: e.target.value })}
                                />
                                {optimalCal?.available && (
                                    <div className="flex items-center justify-between gap-2 rounded-lg bg-emerald-50 border border-emerald-200 px-2.5 py-1.5" data-testid="optimal-calories-hint">
                                        <p className="text-[11px] text-emerald-800 leading-tight">
                                            {language === 'de' ? 'Dein optimaler Bedarf' : 'Your optimal intake'}: <strong>{optimalCal.tdee.toLocaleString('de-CH')} kcal</strong>
                                            <span className="block text-[10px] text-emerald-600">
                                                Mifflin-St-Jeor · {language === 'de' ? 'Grundumsatz' : 'BMR'} {optimalCal.bmr.toLocaleString('de-CH')} × {optimalCal.activity_factor}
                                            </span>
                                        </p>
                                        {parseInt(profileForm.calorie_goal) !== optimalCal.tdee && (
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                className="h-7 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-100 shrink-0"
                                                onClick={() => setProfileForm(prev => ({ ...prev, calorie_goal: String(optimalCal.tdee) }))}
                                                data-testid="apply-optimal-calories-btn"
                                            >
                                                {language === 'de' ? 'Übernehmen' : 'Apply'}
                                            </Button>
                                        )}
                                    </div>
                                )}
                                {optimalCal && !optimalCal.available && (
                                    <p className="text-[10px] text-muted-foreground" data-testid="optimal-calories-missing">
                                        {language === 'de'
                                            ? 'Für die Bedarfs-Berechnung fehlen: Gewicht, Grösse, Alter oder Geschlecht (oben im Profil ergänzen).'
                                            : 'To calculate your optimal intake, add weight, height, age and gender in your profile above.'}
                                    </p>
                                )}
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="water_goal">{t('water_ml')}</Label>
                                <Input
                                    id="water_goal"
                                    type="number"
                                    value={profileForm.water_goal_ml}
                                    onChange={(e) => setProfileForm({ ...profileForm, water_goal_ml: e.target.value })}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="sleep_goal">{t('sleep_hours')}</Label>
                                <Input
                                    id="sleep_goal"
                                    type="number"
                                    step="0.5"
                                    value={profileForm.sleep_goal_hours}
                                    onChange={(e) => setProfileForm({ ...profileForm, sleep_goal_hours: e.target.value })}
                                />
                            </div>
                        </div>
                        
                        {/* Macro Goals (optional — from doctor) */}
                        <div className="border-t pt-4 mt-4">
                            <div className="flex items-center gap-2 mb-3">
                                <Target className="w-5 h-5 text-primary" />
                                <h4 className="font-medium text-sm">{language === 'de' ? 'Makro-Ziele (optional — z.B. vom Arzt)' : 'Macro Goals (optional — e.g. from doctor)'}</h4>
                            </div>
                            <p className="text-xs text-muted-foreground mb-3">
                                {language === 'de' 
                                    ? 'Leer lassen = automatische Berechnung basierend auf Gewicht und Fitnesslevel'
                                    : 'Leave empty = auto-calculated based on weight and fitness level'}
                            </p>
                            <div className="grid grid-cols-3 gap-3">
                                <div className="space-y-1">
                                    <Label className="text-xs">Protein (g)</Label>
                                    <Input type="number" placeholder={language === 'de' ? 'Auto' : 'Auto'}
                                        value={profileForm.protein_goal_g}
                                        onChange={(e) => setProfileForm({ ...profileForm, protein_goal_g: e.target.value })}
                                        className="h-9"
                                    />
                                </div>
                                <div className="space-y-1">
                                    <Label className="text-xs">Carbs (g)</Label>
                                    <Input type="number" placeholder={language === 'de' ? 'Auto' : 'Auto'}
                                        value={profileForm.carbs_goal_g}
                                        onChange={(e) => setProfileForm({ ...profileForm, carbs_goal_g: e.target.value })}
                                        className="h-9"
                                    />
                                </div>
                                <div className="space-y-1">
                                    <Label className="text-xs">{language === 'de' ? 'Fett' : 'Fat'} (g)</Label>
                                    <Input type="number" placeholder={language === 'de' ? 'Auto' : 'Auto'}
                                        value={profileForm.fat_goal_g}
                                        onChange={(e) => setProfileForm({ ...profileForm, fat_goal_g: e.target.value })}
                                        className="h-9"
                                    />
                                </div>
                            </div>

                            {/* Portion preferences for AI estimation (v3.20) */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 pt-4 border-t">
                                <div className="space-y-1">
                                    <Label className="text-xs">{language === 'de' ? 'Portionsgröße' : 'Portion size'}</Label>
                                    <select
                                        value={profileForm.portion_size_pref}
                                        onChange={(e) => setProfileForm({ ...profileForm, portion_size_pref: e.target.value })}
                                        className="h-9 px-3 w-full rounded-md border border-input bg-background text-sm"
                                        data-testid="portion-size-pref"
                                    >
                                        <option value="small">{language === 'de' ? 'Klein (–20%)' : 'Small (–20%)'}</option>
                                        <option value="medium">{language === 'de' ? 'Mittel (Standard)' : 'Medium (default)'}</option>
                                        <option value="large">{language === 'de' ? 'Groß (+20%)' : 'Large (+20%)'}</option>
                                    </select>
                                </div>
                                <div className="space-y-1">
                                    <Label className="text-xs">{language === 'de' ? 'Ernährungsstil' : 'Eating style'}</Label>
                                    <select
                                        value={profileForm.eating_style}
                                        onChange={(e) => setProfileForm({ ...profileForm, eating_style: e.target.value })}
                                        className="h-9 px-3 w-full rounded-md border border-input bg-background text-sm"
                                        data-testid="eating-style"
                                    >
                                        <option value="calorie_conscious">{language === 'de' ? 'Kalorienbewusst (–10%)' : 'Calorie-conscious (–10%)'}</option>
                                        <option value="balanced">{language === 'de' ? 'Ausgewogen' : 'Balanced'}</option>
                                        <option value="bulking">{language === 'de' ? 'Aufbau (+10%)' : 'Bulking (+10%)'}</option>
                                    </select>
                                </div>
                                <p className="md:col-span-2 text-[11px] text-muted-foreground -mt-1">
                                    {language === 'de'
                                        ? 'Beeinflusst alle AI-Schätzungen (Voice/Foto/Text). Nanouk rechnet jetzt konservativer und nutzt USDA-Referenzwerte.'
                                        : 'Affects all AI estimates (voice/photo/text). Nanouk now uses conservative USDA references.'}
                                </p>

                                {/* Sport-Kalorien-Verrechnung (activity_credit_mode) */}
                                <div className="md:col-span-2 space-y-1 pt-3 mt-1 border-t border-dashed border-border">
                                    <Label className="text-xs">
                                        {language === 'de' ? 'Sport-Kalorien anrechnen' : 'Sport calorie credit'}
                                    </Label>
                                    <select
                                        value={profileForm.activity_credit_mode}
                                        onChange={(e) => setProfileForm({ ...profileForm, activity_credit_mode: e.target.value })}
                                        className="h-9 px-3 w-full rounded-md border border-input bg-background text-sm"
                                        data-testid="activity-credit-mode"
                                    >
                                        <option value="strict">
                                            {language === 'de'
                                                ? 'Strikt (0 %) — Sport zählt nicht zur Kalorien-Bilanz'
                                                : 'Strict (0 %) — sport does not count toward calorie balance'}
                                        </option>
                                        <option value="conservative">
                                            {language === 'de'
                                                ? 'Konservativ (50 %) — empfohlen bei Cut / Gewicht halten'
                                                : 'Conservative (50 %) — recommended for cut / maintenance'}
                                        </option>
                                        <option value="liberal">
                                            {language === 'de'
                                                ? 'Liberal (100 %) — 1:1, sinnvoll bei Bulking / Muskelaufbau'
                                                : 'Liberal (100 %) — 1:1, useful for bulking / muscle gain'}
                                        </option>
                                    </select>
                                    <p className="text-[11px] text-muted-foreground">
                                        {language === 'de'
                                            ? 'Wie viel der durch Workouts & Schritte verbrannten Kalorien soll deinem Kalorienziel auf der Ernährung-Seite gutgeschrieben werden? Tippe auf das ⓘ neben „Sport" in der Kalorien-Bilanz für die Wissenschaft dahinter.'
                                            : 'How much of the calories burned via workouts & steps should be credited to your daily goal on the Nutrition page? Tap the ⓘ next to "Sport" in the calorie balance for the science behind it.'}
                                    </p>
                                </div>

                                {/* Taler system (points instead of calories) */}
                                <div className="md:col-span-2 space-y-3 pt-3 mt-1 border-t border-dashed border-border" data-testid="taler-settings-section">
                                    <div className="flex items-start justify-between gap-3">
                                        <div>
                                            <Label className="flex items-center gap-2 text-xs">
                                                <Coins className="w-4 h-4 text-amber-600" />
                                                {language === 'de' ? 'Taler-System' : 'Taler system'}
                                            </Label>
                                            <p className="text-[11px] text-muted-foreground mt-1">
                                                {language === 'de'
                                                    ? 'Punkte statt Kalorien: Jede Mahlzeit kostet Taler (Protein & Ballaststoffe verbilligen, Fett & Kohlenhydrate verteuern). Echte Trainingseinheiten bringen bis zu +4 Bonus-Taler.'
                                                    : 'Points instead of calories: each meal costs Taler (protein & fiber discount, fat & carbs increase the price). Real workouts earn up to +4 bonus Taler.'}
                                            </p>
                                        </div>
                                        <Switch
                                            checked={!!profileForm.taler_enabled}
                                            onCheckedChange={(v) => setProfileForm({ ...profileForm, taler_enabled: v })}
                                            data-testid="taler-enabled-switch"
                                        />
                                    </div>
                                    {profileForm.taler_enabled && (
                                        <div className="space-y-1 max-w-[200px]">
                                            <Label className="text-xs">{language === 'de' ? 'Tagesbudget (Taler)' : 'Daily budget (Taler)'}</Label>
                                            <Input
                                                type="number"
                                                min={8}
                                                max={40}
                                                value={profileForm.taler_daily_budget}
                                                onChange={(e) => setProfileForm({ ...profileForm, taler_daily_budget: e.target.value })}
                                                className="h-9"
                                                data-testid="taler-budget-input"
                                            />
                                            <p className="text-[10px] text-muted-foreground">
                                                {language === 'de' ? 'Empfehlung: 18 · Zielbereich inkl. Sport 18–20 im Wochenschnitt' : 'Recommended: 18 · target range incl. sport 18–20 weekly average'}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Calendar Sync (optional) — iCal/webcal subscription */}
                        <div className="border-t pt-4 mt-4">
                            <CalendarSubscriptionCard />
                        </div>
                        
                        {/* Sleep Defaults Section */}
                        <div className="border-t pt-4 mt-4">
                            <div className="flex items-center gap-2 mb-4">
                                <Moon className="w-5 h-5 text-indigo-500" />
                                <h4 className="font-medium">{language === 'de' ? 'Standard-Schlafzeiten' : 'Default Sleep Times'}</h4>
                            </div>
                            <p className="text-sm text-muted-foreground mb-4">
                                {language === 'de' 
                                    ? 'Diese Zeiten werden täglich vorausgefüllt. Du musst nur noch bestätigen oder Abweichungen anpassen.'
                                    : 'These times are pre-filled daily. You only need to confirm or adjust deviations.'}
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="default_sleep_time">
                                        {language === 'de' ? 'Standard-Schlafenszeit' : 'Default Bedtime'}
                                    </Label>
                                    <Input
                                        id="default_sleep_time"
                                        type="time"
                                        value={profileForm.default_sleep_time}
                                        onChange={(e) => setProfileForm({ ...profileForm, default_sleep_time: e.target.value })}
                                        data-testid="default-sleep-time-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="default_wake_time">
                                        {language === 'de' ? 'Standard-Aufwachzeit' : 'Default Wake Time'}
                                    </Label>
                                    <Input
                                        id="default_wake_time"
                                        type="time"
                                        value={profileForm.default_wake_time}
                                        onChange={(e) => setProfileForm({ ...profileForm, default_wake_time: e.target.value })}
                                        data-testid="default-wake-time-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="default_sleep_quality">
                                        {language === 'de' ? 'Standard-Qualität' : 'Default Quality'}
                                    </Label>
                                    <Input
                                        id="default_sleep_quality"
                                        type="number"
                                        min={1}
                                        max={10}
                                        value={profileForm.default_sleep_quality}
                                        onChange={(e) => setProfileForm({ ...profileForm, default_sleep_quality: e.target.value })}
                                        data-testid="default-sleep-quality-input"
                                    />
                                </div>
                            </div>
                        </div>
                        
                        <Button onClick={handleSave} disabled={loading} className="w-full gap-2">
                            <Save className="w-4 h-4" />
                            {loading ? t('saving') : t('save_changes')}
                        </Button>
                    </CardContent>
                </Card>

        </>
    );
}
